<template>
  <div class="view-all-panel">
    <div class="panel-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="$emit('close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2 class="panel-title">
        Tasks handled automatically
      </h2>
    </div>
    <div class="panel-body">
      <div
        v-if="loading"
        class="panel-loading"
      >
        Loading...
      </div>
      <div
        v-else-if="error"
        class="panel-error"
      >
        Error: {{ error.message }}
      </div>
      <table
        v-else-if="allAutomations.length > 0"
        class="panel-table"
      >
        <thead>
          <tr>
            <th>Automation</th>
            <th class="text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in allAutomations"
            :key="item.workflowId || index"
            :class="{ 'clickable': item.workflowId }"
            @click="item.workflowId ? openWorkflowGuarded(item.workflowId) : null"
          >
            <td>{{ item.name }}</td>
            <td class="text-right">
              {{ item.chargeable }}
            </td>
          </tr>
        </tbody>
      </table>
      <div
        v-else
        class="panel-empty"
      >
        No automation data available
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted } from 'vue'
import { useActionsThisPeriodData } from '../composables/useWidgetData.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

defineEmits(['close'])

const { data, loading, error, cleanup } = useActionsThisPeriodData({ priority: 1 })

const allAutomations = computed(() => {
  const items = data.value?.workflowActionsCount?.data ?? []
  return [...items]
    .sort((a, b) => (b.chargeable ?? 0) - (a.chargeable ?? 0))
    .map((item) => ({
      workflowId: item.workflow?.workflow_id,
      name: item.workflow?.name ?? 'Unknown',
      chargeable: item.chargeable ?? 0
    }))
})

const getUrlParams = () => {
  let clientId = ''
  let canvasUUID = ''
  const urlParams = new URLSearchParams(window.location.search)
  clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (!clientId || !canvasUUID) {
    try {
      if (document.referrer) {
        const referrerParams = new URLSearchParams(new URL(document.referrer).search)
        if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
        if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      }
    } catch (e) { /* ignore */ }
  }
  return { clientId, canvasUUID }
}

const { guard } = useTrialModeGuard()
const openWorkflowGuarded = guard((workflowId) => {
  if (!workflowId) return
  const params = getUrlParams()
  const generateCanvasUUID = () => {
    const timestamp = Date.now()
    const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
    return `${timestamp}-${random}`
  }
  const finalCanvasUUID = params.canvasUUID || generateCanvasUUID()
  const directMessage = {
    workflowId: workflowId.toString(),
    canvasUUID: finalCanvasUUID,
    client_id: params.clientId || undefined
  }
  const commandMessage = { command: 'WorkflowView', payload: directMessage }
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(directMessage, '*')
      setTimeout(() => window.parent.postMessage(commandMessage, '*'), 50)
    }
  } catch (err) {
    console.error('Error posting WorkflowView message:', err)
  }
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.view-all-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  flex-shrink: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-title {
  flex: 1;
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.panel-loading,
.panel-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.panel-error {
  font-size: 14px;
  color: var(--color-red);
}

.panel-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.panel-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.panel-table th.text-right {
  text-align: right;
  width: 80px;
}

.panel-table tbody tr {
  height: 48px;
}

.panel-table td {
  padding: 0 12px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.panel-table td:first-child {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel-table td.text-right {
  text-align: right;
  font-weight: 600;
}

.panel-table tbody tr:last-child td {
  border-bottom: none;
}

.panel-table tbody tr.clickable {
  cursor: pointer;
}

.panel-table tbody tr.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
